import { useMapSelectedVenue } from '@/contexts/MapSelectedVenue'
import type { Maybe } from '@/frontend-types'
import { extent } from 'geojson-bounds'
import mapboxgl from 'mapbox-gl'
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { ID_DATA_SOURCE, ZOOM_MARKER, ZOOM_SPEED } from '../constants'
import {
	getDataId,
	getFeatureId,
	isClusterFeature,
	isMarkerFeature,
	markersToGeometry,
} from '../helpers'
import type {
	IMarkerData,
	MapRef,
	MarkerGeometry,
	QueriedCluster,
	QueriedMarker,
	RenderMarker,
} from '../types'

type ViewportHistory = {
	dirty: boolean
	previous: {
		key: string
		bounds: mapboxgl.LngLatBounds
	} | null
}

function getDataSource(map: mapboxgl.Map) {
	return map.getSource<mapboxgl.GeoJSONSource>(ID_DATA_SOURCE)
}

function getDataSourceFeatures(map: mapboxgl.Map) {
	return map.querySourceFeatures(ID_DATA_SOURCE)
}

function getBoundsKeyFromSourceData(data: MarkerGeometry): string {
	const ids = data.features.map((feature) => feature.properties.id)
	return ids.sort().join('::')
}

function isDataSourceUpdate(event: mapboxgl.MapDataEvent): event is mapboxgl.MapSourceDataEvent {
	return (
		event.dataType === 'source' &&
		event.sourceId === ID_DATA_SOURCE &&
		event.isSourceLoaded === true
	)
}

export default function useMarkers(
	mapRef: MapRef,
	data: IMarkerData[],
	markersContainer?: RefObject<HTMLElement | null>,
): RenderMarker[] {
	const [markers, setMarkers] = useState<RenderMarker[]>([])
	const viewport = useRef<ViewportHistory>({ dirty: true, previous: null })
	const previousSelectedVenue = useRef<string | null>(null)

	const { selectedVenue } = useMapSelectedVenue()

	/**
	 * Handles fitting data source to bounds element
	 */
	const fitBounds = useCallback(() => {
		// Get map
		const map = mapRef.current
		if (!map) return

		// Get data source
		const source = getDataSource(map)
		if (!source) return

		// Get serialised data
		const sourceData = source.serialize().data as Maybe<MarkerGeometry>
		if (!sourceData || typeof sourceData === 'string') return

		// If previous bounds match current key, fit to previous bounds
		const prevView = viewport.current.previous
		if (prevView && getBoundsKeyFromSourceData(sourceData) === prevView.key) {
			map.fitBounds(prevView.bounds, { speed: ZOOM_SPEED })
			return
		}

		// Calc and fit bounds
		try {
			const bounds = extent(sourceData)
			map.fitBounds(bounds, {
				speed: ZOOM_SPEED,
				padding: 64,
				maxZoom: ZOOM_MARKER,
			})
		} catch {
			// Fail gracefully
		}
	}, [mapRef])

	/**
	 * Handles adding and removing marker data to map source
	 */
	useEffect(() => {
		const map = mapRef.current
		if (!map) return

		/**
		 * Handle marker data
		 */
		const updateSourceData = () => {
			const source = getDataSource(map)
			if (source) {
				// Update source with new data
				source.setData(markersToGeometry(data))
				viewport.current.dirty = true
				return true
			}
			return false
		}

		const onSourceData = (event: mapboxgl.MapDataEvent) => {
			if (isDataSourceUpdate(event)) {
				const success = updateSourceData()
				if (success) map.off('sourcedata', onSourceData)
			}
		}

		// If source exists, set data immediately
		// Else, wait for source to load, then set data
		const source = getDataSource(map)
		if (source?.loaded()) updateSourceData()
		else map.on('sourcedata', onSourceData)

		/**
		 * Handle rendering
		 */
		const onRender = () => {
			const source = getDataSource(map)
			if (!source) return

			const allFeatures = getDataSourceFeatures(map)
			const features: (QueriedCluster | QueriedMarker)[] = []

			for (const feature of allFeatures) {
				if (isClusterFeature(feature) || isMarkerFeature(feature)) {
					features.push(feature)
				}
			}

			setMarkers((renderArray) => {
				const featureIds = new Set(features.map(getFeatureId))
				const markerIds = new Set(renderArray.map((m) => getDataId(m.data)))

				/**
				 * Create and update markers
				 */
				for (const feature of features) {
					// Do nothing if marker already exists
					const id = getFeatureId(feature)
					if (markerIds.has(id)) continue

					// Else create new marker
					const [lng, lat] = feature.geometry.coordinates
					const marker = new mapboxgl.Marker(document.createElement('div'))
						.setLngLat([lng, lat])
						.addTo(map)

					// Add cluster click functionality
					if (isClusterFeature(feature)) {
						marker.getElement().addEventListener('click', () => {
							source.getClusterExpansionZoom(feature.properties.cluster_id, (_err, zoom) => {
								if (!zoom) return
								map.flyTo({
									center: [lng, lat],
									zoom,
									speed: ZOOM_SPEED,
								})
							})
						})
					}

					// Add to render array and track that this ID has been created
					markerIds.add(id)
					renderArray.push({
						marker,
						data: feature.properties,
					})
				}

				/**
				 * Delete old markers
				 */
				if (!map._removed) {
					for (const { marker, data } of renderArray) {
						if (!featureIds.has(getDataId(data))) {
							marker.remove()
						}
					}
				}

				// Return render array excluding old markers
				return renderArray.filter((item) => {
					return featureIds.has(getDataId(item.data))
				})
			})
		}
		map.on('render', onRender)

		return () => {
			map.off('sourcedata', onSourceData)
			map.off('render', onRender)
		}
	}, [mapRef, data, markersContainer])

	/**
	 * Handles re-fitting markers on screen resize and data source update
	 */
	useEffect(() => {
		// Handle resize
		let obs: ResizeObserver | undefined
		const container = markersContainer?.current
		if (container) {
			obs = new ResizeObserver(fitBounds)
			obs.observe(container)
		}

		// Handle data source update
		const onSourceData = (event: mapboxgl.MapDataEvent) => {
			if (isDataSourceUpdate(event) && viewport.current.dirty) {
				viewport.current.dirty = false
				fitBounds()
			}
		}
		const map = mapRef.current
		if (map) map.on('sourcedata', onSourceData)

		// Cleanup
		return () => {
			if (obs) obs.disconnect()
			if (map) map.off('sourcedata', onSourceData)
		}
	}, [mapRef, markersContainer, fitBounds])

	/**
	 * Handles re-fitting markers on venue selection
	 */
	useEffect(() => {
		const map = mapRef.current
		if (!map) return

		const prevSelectedVenue = previousSelectedVenue.current

		// Venue was selected
		if (selectedVenue && prevSelectedVenue !== selectedVenue) {
			// Find the selected marker data
			const selectedMarker = data.find((marker) => marker.id === selectedVenue)
			if (selectedMarker) {
				// Save current viewport if we're not already viewing a selected marker
				if (!prevSelectedVenue) {
					const bounds = map.getBounds()
					const source = getDataSource(map)
					if (bounds && source) {
						const sourceData = source.serialize().data as Maybe<MarkerGeometry>
						if (sourceData && typeof sourceData !== 'string' && sourceData.features.length > 0) {
							const key = getBoundsKeyFromSourceData(sourceData)
							viewport.current.previous = { bounds, key }
						}
					}
				}

				// Fly to the selected marker
				map.flyTo({
					padding: { top: 200 },
					retainPadding: false,
					center: [selectedMarker.lng, selectedMarker.lat],
					zoom: Math.max(map.getZoom(), ZOOM_MARKER),
					speed: ZOOM_SPEED,
				})
			}
		}
		// Venue was deselected (popover closed)
		else if (!selectedVenue && prevSelectedVenue) {
			// Restore previous viewport if available
			const prevView = viewport.current.previous
			if (prevView) {
				map.fitBounds(prevView.bounds, {
					speed: ZOOM_SPEED,
				})
			} else {
				// If no previous viewport, fit to all markers
				fitBounds()
			}
		}

		// Update the previous selected venue ref
		previousSelectedVenue.current = selectedVenue ?? null
	}, [mapRef, data, markersContainer, selectedVenue, fitBounds])

	/**
	 * Return markers for rendering
	 */
	return markers
}
